'use client';

import { type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from './common/Button';

function SocialLink({ href, label, children }: { href: string; label: string; children: ReactNode }) {
  return (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noopener noreferrer"
      className="size-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
    >
      {children}
    </a>
  );
}

function LinkedInIcon() {
  return (
    <svg className="size-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg className="size-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
}

export function Footer() {
  const t = useTranslations('footer');
  const tc = useTranslations('common');
  const locale = useLocale();

  const navLinks = [
    { label: t('nav_work'),     href: `/${locale}/work`      },
    { label: t('nav_ai'),       href: `/${locale}/ai-models` },
    { label: t('nav_about'),    href: `/${locale}/about`     },
    { label: t('nav_team'),     href: `/${locale}/about#team` },
    { label: t('nav_careers'),  href: `/${locale}/careers`   },
    { label: t('nav_articles'), href: `/${locale}/articles`  },
  ];

  const legalLinks = [
    { label: t('nav_imprint'), href: `/${locale}/imprint` },
    { label: t('nav_privacy'), href: `/${locale}/privacy` },
    { label: t('nav_terms'),   href: `/${locale}/terms`   },
  ];

  return (
    <footer data-header-dark className="cv-auto bg-background-secondary flex flex-col overflow-hidden">

      {/* ── Upper: all text content ── */}
      <div className="flex flex-col gap-10 px-[5%] pt-16 pb-10">

        {/* Main row */}
        <div className="flex flex-col gap-8">
          {/* Desktop row 1: title | socials + CTA */}
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row">
            <h2 className="text-[length:var(--text-section-title)] text-font-primary font-normal tracking-[-0.02em] leading-[1.1]">
              {t('cta_line1')}<br />{t('cta_line2')}
            </h2>

            <div className="hidden items-center gap-10 sm:flex">
              <div className="flex items-center gap-4">
                <SocialLink href={tc('linkedin')} label="LinkedIn">
                  <span className="text-white"><LinkedInIcon /></span>
                </SocialLink>
                <SocialLink href={tc('youtube')} label="YouTube">
                  <span className="text-white"><YouTubeIcon /></span>
                </SocialLink>
              </div>
              <Button variant="light" text={t('cta')} url={`/${locale}/contact`} />
            </div>
          </div>

          {/* Desktop row 2: contacts */}
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div className="hidden items-center gap-10 sm:flex">
              <span className="flex items-center gap-3 text-nav text-font-primary">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/icons/tag-user.svg" alt="" className="size-5 shrink-0 invert" />
                {tc('location')}
              </span>
              <a
                href={`mailto:${tc('email')}`}
                className="flex items-center gap-3 text-nav text-font-primary hover:opacity-70 transition-opacity"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/icons/sms-notification.svg" alt="" className="size-5 shrink-0 invert" />
                {tc('email')}
              </a>
            </div>
          </div>

          {/* Mobile block: CTA -> contacts -> socials */}
          <div className="flex w-full flex-col items-start gap-6 sm:hidden">
            <div className="w-full items-center">
              <Button variant="light" text={t('cta')} url={`/${locale}/contact`} />
            </div>

            <div className="flex flex-col items-start gap-3">
              <span className="flex items-center gap-3 text-nav text-font-primary">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/icons/tag-user.svg" alt="" className="size-5 shrink-0 invert" />
                {tc('location')}
              </span>
              <a
                href={`mailto:${tc('email')}`}
                className="flex items-center gap-3 text-nav text-font-primary"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/icons/sms-notification.svg" alt="" className="size-5 shrink-0 invert" />
                {tc('email')}
              </a>
            </div>

            <div className="flex gap-4">
              <SocialLink href={tc('linkedin')} label="LinkedIn">
                <span className="text-white"><LinkedInIcon /></span>
              </SocialLink>
              <SocialLink href={tc('youtube')} label="YouTube">
                <span className="text-white"><YouTubeIcon /></span>
              </SocialLink>
            </div>
          </div>
        </div>

        {/* Nav links row */}
        <div className="flex flex-col xl:flex-row xl:justify-between gap-6 border-t border-white/10 pt-8">
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-nav text-font-primary/55 hover:text-font-primary/90 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {legalLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-nav text-font-primary/40 hover:text-font-primary/70 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Legal entities — locale-aware */}
        <div className="flex flex-col gap-2 border-t border-white/10 pt-8">
          {locale === 'cs' ? (
            <p className="text-nav text-font-primary/35">{t('entity_sro')}</p>
          ) : locale === 'de' ? (
            <p className="text-nav text-font-primary/35">{t('entity_gmbh')}</p>
          ) : (
            <>
              <p className="text-nav text-font-primary/35">{t('entity_gmbh')}</p>
              <p className="text-nav text-font-primary/35">{t('entity_sro')}</p>
            </>
          )}
        </div>

        {/* Copyright */}
        <p className="text-nav text-font-primary/65">{t('copyright')}</p>

      </div>

      {/* ── Lower: large brand logo, fades out at bottom ── */}
      <div className="overflow-hidden select-none" style={{ height: 'calc(100vw * 16 / 92)', maxHeight: '300px' }} aria-hidden="true">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/Ollsoft-logo.svg"
          alt=""
          className="w-full opacity-[0.2]"
          style={{
            maskImage: 'linear-gradient(to bottom, black 10%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 10%, transparent 100%)',
          }}
        />
      </div>

    </footer>
  );
}
